import type { QueryClient, QueryKey } from '@tanstack/react-query';

/**
 * Invalidates queries for a realtime update without aborting requests that were already running.
 * Those concrete queries are invalidated again after settling, so active queries refetch and
 * inactive queries remain stale for their next observer. Queries started by this invalidation are
 * not invalidated a second time.
 */
export async function invalidateRealtimeQueries(
  queryClient: QueryClient,
  queryKey: QueryKey,
): Promise<void> {
  const inFlightQueries = queryClient
    .getQueryCache()
    .findAll({ queryKey, fetchStatus: 'fetching' });
  const inFlightQueryHashes = new Set(inFlightQueries.map((query) => query.queryHash));

  if (inFlightQueryHashes.size === 0) {
    await queryClient.invalidateQueries({ queryKey });
    return;
  }

  void queryClient.invalidateQueries({ queryKey }, { cancelRefetch: false });
  await Promise.allSettled(inFlightQueries.map(async (query) => query.promise));
  await queryClient.invalidateQueries(
    {
      predicate: (query) => inFlightQueryHashes.has(query.queryHash),
    },
    { cancelRefetch: false },
  );
}
